<template>
  <div class="step-two">
    <h2 class="text-h6 mb-4">{{ $t('onboarding.step2.description') }}</h2>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">
      {{ error }}
    </v-alert>

    <!-- Display mode: IBAN already entered -->
    <div v-if="hasExistingIban && !editMode">
      <v-card outlined class="pa-4 mb-4">
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="text-h6">{{ $t('onboarding.existing-payment-info') }}</div>
          <v-btn
            small
            outlined
            color="primary"
            @click="editMode = true"
          >
            <v-icon left small>mdi-pencil</v-icon>
            {{ $t('onboarding.change-iban') }}
          </v-btn>
        </div>

        <v-divider class="mb-3"></v-divider>

        <!-- Billing Details -->
        <div v-if="paymentMethodDetails.billingDetails">
          <div class="mb-3">
            <div class="text-caption text--secondary">{{ $t('payment-method-ui.debtor-name') }}</div>
            <div class="text-body-1">{{ paymentMethodDetails.billingDetails.name || '-' }}</div>
          </div>
          <div class="mb-3">
            <div class="text-caption text--secondary">{{ $t('payment-method-ui.debtor-email') }}</div>
            <div class="text-body-1">{{ paymentMethodDetails.billingDetails.email || '-' }}</div>
          </div>
        </div>

        <!-- IBAN -->
        <div class="mb-2">
          <div class="text-caption text--secondary">{{ $t('payment-method-ui.iban') }}</div>
          <div class="text-h6">
            {{ formatIban(paymentMethodDetails) }}
          </div>
          <div v-if="paymentMethodDetails.country" class="text-caption text--secondary mt-1">
            {{ $t('country') }}: {{ paymentMethodDetails.country }}
          </div>
        </div>
      </v-card>

      <div class="d-flex justify-space-between mt-6">
        <v-btn
          text
          small
          :disabled="loading"
          @click="$emit('back')"
        >
          <v-icon left small>mdi-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          color="primary"
          small
          :loading="loading"
          @click="handleNextWithExisting"
        >
          {{ $t('next') }}
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Edit mode: Enter new IBAN -->
    <div v-else>
      <v-alert v-if="hasExistingIban" type="info" dense outlined class="mb-4">
        {{ $t('onboarding.entering-new-iban') }}
      </v-alert>

      <!-- Organization info (readonly) for organizations -->
      <template v-if="isOrganization">
        <v-card outlined class="pa-3 mb-4">
          <div class="text-caption text--secondary mb-1">{{ $t('company-name') }}</div>
          <div class="text-body-1 mb-2">{{ enrollment.companyName || '-' }}</div>
          <div class="text-caption text--secondary mb-1">{{ $t('email') }}</div>
          <div class="text-body-1">{{ enrollment.email || '-' }}</div>
        </v-card>
      </template>

      <!-- Signatory section -->
      <div class="text-subtitle-2 mb-2">
        {{ $t('payment-method-ui.signatory-info') }} *
      </div>
      <v-alert type="info" dense outlined class="mb-3">
        <div class="text-caption">
          {{ $t('payment-method-ui.signatory-hint') }}
        </div>
      </v-alert>

      <v-row class="mb-3">
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="firstName"
            :label="$t('firstname') + ' *'"
            outlined
            dense
            required
            placeholder="Martin"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            v-model="lastName"
            :label="$t('lastname') + ' *'"
            outlined
            dense
            required
            placeholder="Dupond"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Email (readonly - comes from enrollment, cannot be changed) -->
      <v-text-field
        v-model="email"
        :label="$t('email')"
        outlined
        dense
        readonly
        disabled
        class="mb-4"
        :hint="$t('onboarding.email-readonly-hint')"
        persistent-hint
      ></v-text-field>

      <v-divider class="my-4"></v-divider>

      <div class="mb-2 text-subtitle-2">{{ $t('payment-method-ui.iban') }}</div>
      <div
        id="onboarding-iban-element"
        style="border: 1px solid #ccc; border-radius: 4px; padding: 10px; background: white;"
      ></div>
      <div v-if="ibanError" class="error--text text-caption mt-1">{{ ibanError }}</div>

      <div class="d-flex justify-space-between mt-6">
        <v-btn
          text
          small
          :disabled="loading"
          @click="handleBackButton"
        >
          <v-icon left small>mdi-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn
          color="primary"
          small
          :loading="loading || creating"
          :disabled="!firstName || !lastName || !email || creating"
          @click="handleNext"
        >
          {{ $t('next') }}
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import StripeService from '@service/StripeService'

export default {
  name: 'StepTwo',

  props: {
    loading: {
      type: Boolean,
      default: false
    },
    enrollment: {
      type: Object,
      default: null
    },
    existingPaymentMethodId: {
      type: String,
      default: null
    },
    paymentMethodDetails: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      stripe: null,
      elements: null,
      ibanElement: null,
      firstName: '',
      lastName: '',
      email: '',
      ibanError: null,
      creating: false,
      error: null,
      editMode: false
    }
  },

  computed: {
    hasExistingIban() {
      return !!(this.existingPaymentMethodId && this.paymentMethodDetails?.last4)
    },
    isOrganization() {
      return this.enrollment?.type === 'organization'
    }
  },

  watch: {
    editMode(val) {
      if (val) {
        // When switching to edit mode, initialize Stripe if needed then mount the IBAN element
        this.$nextTick(async () => {
          if (!this.stripe) {
            await this.initStripe()
          } else {
            this.mountIbanElement()
          }
        })
      }
    },
    enrollment: {
      immediate: true,
      handler(val) {
        // Pre-fill signer info from enrollment if available
        if (val) {
          if (!this.firstName && val.firstName) {
            this.firstName = val.firstName
          }
          if (!this.lastName && val.lastName) {
            this.lastName = val.lastName
          }
          // Email is always from enrollment (readonly field)
          if (val.email) {
            this.email = val.email
          }
        }
      }
    }
  },

  mounted() {
    // Only init Stripe if we don't have an existing IBAN or we're in edit mode
    if (!this.hasExistingIban || this.editMode) {
      this.initStripe()
    }
  },

  beforeDestroy() {
    this.destroyIbanElement()
  },

  methods: {
    async initStripe() {
      try {
        this.stripe = await StripeService.initStripe()

        if (!this.elements && this.stripe) {
          this.elements = this.stripe.elements()
        }

        this.$nextTick(() => {
          this.mountIbanElement()
        })
      } catch (err) {
        this.error = this.$t('onboarding.error.stripe-init-failed')
      }
    },

    mountIbanElement() {
      try {
        this.destroyIbanElement()

        if (!this.elements) return

        const style = {
          base: {
            fontSize: '16px',
            color: '#32325d',
            '::placeholder': {
              color: '#aab7c4'
            }
          },
          invalid: {
            color: '#fa755a'
          }
        }

        this.ibanElement = this.elements.create('iban', {
          style,
          supportedCountries: ['SEPA']
        })

        const container = document.getElementById('onboarding-iban-element')
        if (container) {
          this.ibanElement.mount(container)
          this.ibanElement.on('change', (ev) => {
            this.ibanError = ev.error ? ev.error.message : null
          })
        }
      } catch (e) {
        this.ibanError = e?.message || String(e)
      }
    },

    destroyIbanElement() {
      try {
        if (this.ibanElement) {
          this.ibanElement.unmount()
          this.ibanElement.destroy()
        }
      } finally {
        this.ibanElement = null
      }
    },

    formatIban(details) {
      if (!details || !details.last4) return ''
      const country = details.country || '**'
      const last4 = details.last4
      return `${country}** **** **** **** **${last4}`
    },

    handleNextWithExisting() {
      // Use existing payment method ID
      this.$emit('next', this.existingPaymentMethodId)
    },

    handleBackButton() {
      if (this.editMode && this.hasExistingIban) {
        // Cancel edit mode
        this.editMode = false
      } else {
        // Go back to previous step
        this.$emit('back')
      }
    },

    async handleNext() {
      if (!this.stripe || !this.ibanElement) {
        this.error = this.$t('onboarding.error.stripe-not-ready')
        return
      }

      if (!this.firstName || !this.lastName || !this.email) {
        this.error = this.$t('rules.field-required')
        return
      }

      this.creating = true
      this.ibanError = null
      this.error = null

      try {
        // Créer le PM pour avoir l'ID (nécessaire pour prepare-view du mandat)
        const { paymentMethod, error } = await this.stripe.createPaymentMethod({
          type: 'sepa_debit',
          sepa_debit: this.ibanElement,
          billing_details: {
            name: `${this.firstName} ${this.lastName}`,
            email: this.email || undefined
          }
        })

        if (error) {
          this.ibanError = error.message
          return
        }

        if (paymentMethod?.id) {
          // Emit paymentMethod.id and signer info
          // SetupIntent will be created in PublicOnboarding.handleStep2Next
          this.$emit('next', {
            paymentMethodId: paymentMethod.id,
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email
          })
        } else {
          this.error = this.$t('onboarding.error.payment-method-failed')
        }
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || String(e)
      } finally {
        this.creating = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.step-two {}
</style>
